import React, { useState, useEffect } from 'react';
import './App.css';
import { cardsData } from './cardsData';
import { saveCompletedDeck, getTotalCompletedDecks, checkPremiumAccess } from "./firebaseUtils.js";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './Home';
import Workout from './Workout';
import GoalsPage from './Goals/GoalsPage';
import GrindIntro from './GrindIntroPage';
import NavBar from './NavBar';
import Login from './Login';
import FargoCampaign from './FargoCampaign';
import PremiumUnlock from './PremiumUnlock';
import PremiumContent from './PremiumContent';
import { auth } from './firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [deckType, setDeckType] = useState('full');
  const [deck, setDeck] = useState([]);
  const [totalDecksCompleted, setTotalDecksCompleted] = useState(0);
  const [milestoneMessage, setMilestoneMessage] = useState("");
  const [drawnCards, setDrawnCards] = useState([]);
  const [started, setStarted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [currentCard, setCurrentCard] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      if (firebaseUser) {
        const total = await getTotalCompletedDecks();
        setTotalDecksCompleted(total);
        const premium = await checkPremiumAccess();
        setIsPremium(premium);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (milestoneMessage) {
      const timer = setTimeout(() => setMilestoneMessage(""), 8000);
      return () => clearTimeout(timer);
    }
  }, [milestoneMessage]);

  const handleSignOut = () => signOut(auth);

  const handlePremiumUnlock = async () => {
    const premium = await checkPremiumAccess();
    setIsPremium(premium);
  };

  const updateWorkoutHistory = async () => {
    await saveCompletedDeck(deckType);
    const total = await getTotalCompletedDecks();
    setTotalDecksCompleted(total);
    if (total === 10) setMilestoneMessage("🎯 10 workouts! You got 1% better!");
    else if (total === 25) setMilestoneMessage("🔥 25 Decks done! Keep grinding!");
    else if (total === 50) setMilestoneMessage("💪 50 Decks - way to Stay On the Grind");
    else if (total === 100) setMilestoneMessage("🏆 Boom! 100 decks. New state champ!!");
  };

  const onCompleteWorkout = () => {
    updateWorkoutHistory();
  };

  function shuffleDeck(deck) {
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    return deck;
  }

  const startWorkout = () => {
    let fullDeck = shuffleDeck([...cardsData]);
    const isJoker = (card) => card.suit === 'Joker';
    const firstCard = fullDeck[0];
    if (isJoker(firstCard)) {
      const nonJokerIndex = fullDeck.findIndex(card => !isJoker(card));
      if (nonJokerIndex !== -1) {
        [fullDeck[0], fullDeck[nonJokerIndex]] = [fullDeck[nonJokerIndex], fullDeck[0]];
      }
    }
    const selectedDeck = deckType === 'half' ? fullDeck.slice(0, 26) : fullDeck;
    setDeck(selectedDeck);
    setDrawnCards([]);
    setCurrentCard(null);
    setShowBack(true);
    setIsFlipped(false);
    setStarted(true);
    setMilestoneMessage("");
    setProgress(0);
  };

  const drawCard = () => {
    if (deck.length === 0 || animating) return;
    setAnimating(true);
    setIsFlipped(false);
    setTimeout(() => {
      const nextCard = deck[0];
      const updatedDrawn = [...drawnCards, nextCard];
      const updatedDeck = deck.slice(1);
      setDrawnCards(updatedDrawn);
      setDeck(updatedDeck);
      setCurrentCard(nextCard);
      const totalCards = deckType === 'half' ? 26 : 54;
      setProgress(Math.round((updatedDrawn.length / totalCards) * 100));
      setTimeout(() => {
        setIsFlipped(true);
        setAnimating(false);
      }, 600);
    }, 400);
  };

  const resetWorkout = () => {
    setDeck([]);
    setDrawnCards([]);
    setCurrentCard(null);
    setStarted(false);
    setDeckType('full');
    setProgress(0);
    setIsFlipped(false);
    setShowBack(false);
  };

  const fontSize = isMobile ? '1rem' : '1.2rem';
  const buttonPadding = isMobile ? '10px 16px' : '12px 24px';

  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: '1.5rem',
        fontFamily: 'Bangers, sans-serif',
        letterSpacing: '2px',
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Router>
      <NavBar user={user} onSignOut={handleSignOut} isPremium={isPremium} />
      <Routes>
        {/* Public routes — no login required */}
        <Route path="/fargo" element={<FargoCampaign />} />
        <Route path="/unlock" element={<PremiumUnlock onUnlock={handlePremiumUnlock} />} />
        <Route path="/premium" element={<PremiumContent />} />

        {/* Auth routes — login required */}
        {!user ? (
          <>
            <Route path="*" element={<Login />} />
          </>
        ) : (
          <>
            <Route path="/" element={<Home user={user} />} />
            <Route
              path="/grind-cards"
              element={
                <div className="App" style={{ textAlign: 'center', paddingTop: '70px' }}>
                  <Workout
                    started={started}
                    drawCard={drawCard}
                    resetWorkout={resetWorkout}
                    progress={progress}
                    deck={deck}
                    deckType={deckType}
                    setDeckType={setDeckType}
                    totalDecksCompleted={totalDecksCompleted}
                    milestoneMessage={milestoneMessage}
                    showHistory={showHistory}
                    setShowHistory={setShowHistory}
                    isMobile={isMobile}
                    currentCard={currentCard}
                    isFlipped={isFlipped}
                    showBack={showBack}
                    fontSize={fontSize}
                    buttonPadding={buttonPadding}
                    startWorkout={startWorkout}
                    onCompleteWorkout={onCompleteWorkout}
                  />
                </div>
              }
            />
            <Route path="/grind-intro" element={<GrindIntro />} />
            <Route path="/goals" element={<GoalsPage user={user} />} />
          </>
        )}
      </Routes>
    </Router>
  );
}

export default App;